import Header from "~/components/ui/SectionHeader"
import Slider from "~/components/ui/Slider"
import SliderJS from "~/components/ui/SliderJS"
import { useId } from "react"
import type { ImageWidget } from "~/types/widgets"
import Image from "~/components/ui/Image"

/**
 * @titleBy label
 */
export interface Category {
    /**
     * @title Tag
     */
    tag?: string
    /**
     * @title Texto
     */
    label: string
    /**
     * @title Descrição
     */
    description?: string
    /**
     * @title Link
     */
    href?: string
    /**
     * @title Imagem
     */
    image?: ImageWidget
    /**
     * @title Texto do botão
     */
    buttonText?: string
}

export interface Props {
    /**
     * @title Cabeçalho
     */
    header?: {
        /**
         * @title Título
         */
        title?: string
        /**
         * @title Descrição
         */
        description?: string
    }
    /**
     * @title Lista de categorias
     */
    list?: Category[]
    layout?: {
        /**
         * @title Alinhamento do cabeçalho
         */
        headerAlignment?: 'center' | 'left'
        /**
         * @title Cartão da categoria
         */
        categoryCard?: {
            /**
             * @title Posição do texto
             */
            textPosition?: 'top' | 'bottom'
            /**
             * @title Alinhamento do texto
             */
            textAlignment?: 'center' | 'left'
        }
    }
}

function CardText({
    tag,
    label,
    description,
    alignment,
}: {
    tag?: string
    label?: string
    description?: string
    alignment?: 'center' | 'left'
}) {
    return (
        <div className={`flex flex-col ${alignment === 'center' ? 'text-center' : 'text-left'}`}>
            {tag && <div className='text-sm text-primary'>{tag}</div>}
            {label && <h3 className='text-lg text-base-content'>{label}</h3>}
            {description && <div className='text-sm text-neutral'>{description}</div>}
        </div>
    )
}

function CategoryList(props: Props) {
    const id = useId()
    const {
        header = {
            title: '',
            description: '',
        },
        list = [
            {
                tag: '10% off',
                label: 'Feminino',
                description: 'Moda feminina direto de Milão',
                href: '/feminino',
                image:
                    'https://decoims.com/miess-01/8783287c-aa79-4e4b-a5d9-46b7c5d047c6/fdcb3c8f-d629-485e-bf70-8060bd8a9f65.jpg',
                buttonText: 'Ver produtos',
            },
        ],
        layout = {
            headerAlignment: 'center',
            categoryCard: {
                textPosition: 'top',
                textAlignment: 'center',
            },
        },
    } = props

    return (
        <div
            data-prerender
            id={id}
            className='container py-8 flex flex-col gap-8 lg:gap-10 text-base-content  lg:py-10'
        >
            <Header
                title={header.title}
                description={header.description || ''}
                alignment={layout.headerAlignment || 'center'}
            />

            <Slider className='carousel carousel-start gap-4 lg:gap-8 row-start-2 row-end-5'>
                {list.map(({ tag, label, description, href, image, buttonText }, index) => (
                    <Slider.Item
                        index={index}
                        className='flex flex-col gap-4 carousel-item first:pl-6 sm:first:pl-0 last:pr-6 sm:last:pr-0'
                    >
                        <a href={href} className='flex flex-col gap-4 lg:w-[280px] w-40 lg:h-auto'>
                            {layout.categoryCard?.textPosition === 'top' && (
                                <CardText
                                    tag={tag}
                                    label={label}
                                    description={description}
                                    alignment={layout?.categoryCard?.textAlignment}
                                />
                            )}
                            {image && (
                                <figure>
                                    <Image
                                        className='card w-full'
                                        src={image}
                                        alt={description || label || tag}
                                        width={160}
                                        height={195}
                                        loading='lazy'
                                    />
                                </figure>
                            )}
                            {layout.categoryCard?.textPosition === 'bottom' && (
                                <CardText
                                    tag={tag}
                                    label={label}
                                    description={description}
                                    alignment={layout?.categoryCard?.textAlignment}
                                />
                            )}
                        </a>
                        {buttonText && (
                            <a href={href} className='btn'>
                                {buttonText}
                            </a>
                        )}
                    </Slider.Item>
                ))}
            </Slider>

            <SliderJS rootId={id} />
        </div>
    )
}

export default CategoryList

export { default as LoadingFallback } from "~/components/LoadingFallback"
